import { FormControl, FormHelperText, FormLabel, Grid, GridItem, Heading, useMediaQuery } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useUpdateOrganizationMutation } from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { Layout } from "../../../../common/layout/Layout";
import { PersonsSelector } from "../../../../common/persons/PersonsSelector";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { updateSelectedOrg } from "../../../../common/redux/reducers/authStateReducer";
import { CONTENT_WIDTH } from "../../../../config";
import { ChangeAboutUs } from "./ChangeAboutUs";
import { ChangeCity } from "./ChangeCity";
import { ChangeContactEmail } from "./ChangeContactEmail";
import { ChangeContactPhone } from "./ChangeContactPhone";
import { ChangeCountry } from "./ChangeCountry";
import { ChangeHomePageUrl } from "./ChangeHomePageUrl";
import { ChangeName } from "./ChangeName";
import { ChangeOrgNumber } from "./ChangeOrgNumber";
import { ChangePostCode } from "./ChangePostCode";
import { ChangeSize } from "./ChangeSize";
import { ChangeSpecializations } from "./ChangeSpecializations";
import { ChangeStreet } from "./ChangeStreet";

export interface EditCompanyProfileFormValues {
  name: string;
  about: string;
  specialties: string;
}

export const EditOrganizationProfile = ({ embedded = false }: { embedded?: boolean }) => {
  const dispatch = useAppDispatch();
  const [isLargerThan768] = useMediaQuery("(min-width: 768px)");
  const authState = useLoggedInWithOrgContextState();
  const { t } = useTranslation();
  const [editOrg] = useUpdateOrganizationMutation();
  const content = (
    <Grid h="100%" gridTemplateColumns={"1fr"} gridTemplateRows={"70px 1fr"}>
      <GridItem display="flex" alignItems="center" borderBottom={"1px solid"} borderColor="smBorder">
        <Heading as="h2" size="lg">
          {t("Edit company profile")}
        </Heading>
      </GridItem>
      <GridItem paddingBottom={"70px"}>
        <ChangeName currentValue={authState.selectedOrg.name} />
        <ChangeCountry currentValue={authState.selectedOrg.country.id} />
        <ChangeOrgNumber currentValue={authState.selectedOrg.organizationNumber ?? undefined} />
        <ChangeHomePageUrl currentValue={authState.selectedOrg.homePageUrl ?? ""} />
        <ChangeAboutUs currentValue={authState.selectedOrg.aboutUs ?? ""} />
        <ChangeSpecializations currentValue={authState.selectedOrg.specializations ?? ""} />
        <Heading as="h3" size="md" mt="6" pt="4" borderTop="1px solid" borderColor="smBorder">
          {t("Address")}
        </Heading>
        <ChangeStreet currentValue={authState.selectedOrg.street ?? ""} />
        <ChangeCity currentValue={authState.selectedOrg.city ?? ""} />
        <ChangePostCode currentValue={authState.selectedOrg.postCode ?? ""} />
        <FormControl pt="4">
          <FormLabel>{t("Contact persons")}</FormLabel>
          <PersonsSelector
            organizationIds={[authState.selectedOrg.id]}
            selectedPersons={authState.selectedOrg.contactPersons ?? undefined}
            onChange={async (values) => {
              const org = await editOrg({
                orgId: authState.selectedOrg.id,
                editOrganizationRequest: {
                  contactPersons: { persons: values.map((p) => p.id) },
                },
              }).unwrap();
              dispatch(updateSelectedOrg(org));
            }}
          />
          <FormHelperText>{`${t("Select contact persons for your organization")}. ${t(
            "These will be visible to other organizations"
          )}.`}</FormHelperText>
        </FormControl>
        <ChangeSize currentValue={authState.selectedOrg.size ?? null} />
        <Heading as="h3" size="md" mt="6" pt="4" borderTop="1px solid" borderColor="smBorder">
          {t("Contact info")}
        </Heading>
        <ChangeContactEmail currentValue={authState.selectedOrg.contactEmail ?? ""} />
        <ChangeContactPhone currentValue={authState.selectedOrg.contactPhone ?? null} />
      </GridItem>
    </Grid>
  );
  return embedded ? (
    content
  ) : (
    <Layout>
      <Grid
        height="100%"
        gridTemplateColumns={`1fr minmax(0, ${CONTENT_WIDTH}) 1fr`}
        gridTemplateRows={"1fr"}
        templateAreas={`
                "leftContent content rightContent"
                `}
        px="2"
      >
        <GridItem area={"content"} px={isLargerThan768 ? "0" : "2.5"}>
          {content}
        </GridItem>
      </Grid>
    </Layout>
  );
};
